import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import AshdaleLogoSVG from '../../assets/ashdale-logo.svg';

const { FiUpload, FiDatabase, FiPower, FiShield, FiAlertTriangle, FiCheckCircle, FiChevronRight } = FiIcons;

const AdminDashboard = () => {
  const [activeMenu, setActiveMenu] = useState('upload');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const menuItems = [
    { id: 'upload', label: 'Upload Ashdale Logo', icon: FiUpload },
    { id: 'data', label: 'Update Data', icon: FiDatabase },
    { id: 'maintenance', label: 'Take System Offline for Maintenance', icon: FiPower },
  ];

  const handleUpdateData = () => {
    setIsUpdating(true);
    setUpdateSuccess(false);
    setTimeout(() => {
      setIsUpdating(false);
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
    }, 2000);
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'upload':
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="bg-secondary-900/50 p-8 rounded-2xl border border-primary-500/10">
              <h3 className="text-xl font-bold text-white mb-2">Brand Identity Management</h3>
              <p className="text-secondary-400 mb-8">Replace the primary system logo across all user interfaces.</p>
              
              <div className="border-2 border-dashed border-secondary-700 rounded-2xl p-16 text-center hover:border-primary-500/50 transition-all cursor-pointer group bg-black/20">
                <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiUpload} className="w-8 h-8 text-primary-500" />
                </div>
                <p className="text-white font-medium">Click to upload or drag and drop</p>
                <p className="text-xs text-secondary-500 mt-2">SVG format preferred (max. 512KB)</p>
              </div>

              <div className="mt-10 pt-10 border-t border-secondary-800">
                <h4 className="text-sm font-semibold text-secondary-500 uppercase tracking-wider mb-6">Currently Active Logo</h4>
                <div className="bg-black p-8 rounded-xl border border-secondary-800 inline-block shadow-inner">
                  <img src={AshdaleLogoSVG} alt="Current Logo" className="h-12" />
                </div>
              </div>
            </div>
          </motion.div>
        );
      case 'data':
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="bg-secondary-900/50 p-8 rounded-2xl border border-primary-500/10">
              <h3 className="text-xl font-bold text-white mb-2">Market Data Refresh</h3>
              <p className="text-secondary-400 mb-8">Pull the latest financial indices and inflation data into the calculation engine.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="p-6 bg-black rounded-xl border border-secondary-800">
                  <p className="text-xs text-secondary-500 uppercase tracking-widest mb-2">Last Successful Sync</p>
                  <p className="text-white font-mono text-lg">2024-05-20 08:45:12</p>
                </div>
                <div className="p-6 bg-black rounded-xl border border-secondary-800">
                  <p className="text-xs text-secondary-500 uppercase tracking-widest mb-2">Cluster Status</p>
                  <div className="flex items-center text-green-500">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mr-2" />
                    <span className="text-sm font-bold">ALL SYSTEMS NOMINAL</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={handleUpdateData}
                disabled={isUpdating}
                className="w-full md:w-auto flex items-center justify-center space-x-3 bg-primary-500 hover:bg-primary-400 text-black px-8 py-4 rounded-xl font-bold transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-primary-500/20"
              >
                {isUpdating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                    <span>Synchronizing Global Indices...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiDatabase} className="w-5 h-5" />
                    <span>Trigger Global Data Update</span>
                  </>
                )}
              </button>

              <AnimatePresence>
                {updateSuccess && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-6 p-4 bg-green-900/20 border border-green-500/30 rounded-xl text-green-400 flex items-center shadow-lg"
                  >
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mr-3" />
                    <span className="font-medium">Financial data indices updated successfully across all clusters.</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        );
      case 'maintenance':
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="bg-secondary-900/50 p-8 rounded-2xl border border-primary-500/10">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">System Maintenance</h3>
                  <p className="text-secondary-400">Control the accessibility of the application platform.</p>
                </div>
                <div className={`px-4 py-1.5 rounded-full text-xs font-black tracking-tighter border ${maintenanceMode ? 'bg-red-900/20 text-red-500 border-red-500/30' : 'bg-green-900/20 text-green-500 border-green-500/30'}`}>
                  {maintenanceMode ? 'OFFLINE' : 'ONLINE'}
                </div>
              </div>

              <div className="bg-red-950/20 p-8 rounded-2xl border border-red-500/20 mb-10">
                <div className="flex items-center space-x-4 mb-4 text-red-500">
                  <SafeIcon icon={FiAlertTriangle} className="w-6 h-6" />
                  <h4 className="font-black text-lg uppercase tracking-tight">Danger Zone</h4>
                </div>
                <p className="text-secondary-400 leading-relaxed mb-8">
                  Taking the system offline will immediately terminate all active sessions and display a maintenance notice to all users. This action is logged.
                </p>
                
                <button 
                  onClick={() => setMaintenanceMode(!maintenanceMode)}
                  className={`w-full flex items-center justify-center space-x-3 px-8 py-5 rounded-2xl font-black transition-all active:scale-[0.98] shadow-xl ${
                    maintenanceMode 
                      ? 'bg-green-600 hover:bg-green-500 text-white' 
                      : 'bg-red-600 hover:bg-red-500 text-white shadow-red-500/10'
                  }`}
                >
                  <SafeIcon icon={FiPower} className="w-5 h-5" />
                  <span>{maintenanceMode ? 'RESTORE SYSTEM ACCESS' : 'TAKE SYSTEM OFFLINE FOR MAINTENANCE'}</span>
                </button>
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-bold text-secondary-500 uppercase tracking-widest pl-1">Recent Maintenance Events</h4>
                <div className="space-y-2">
                  {[
                    { date: '2024-05-15', user: 'Carlo Gunter', action: 'Schema Update' },
                    { date: '2024-04-22', user: 'Carlo Gunter', action: 'Logo Modification' }
                  ].map((log, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-black/40 rounded-xl border border-secondary-800 text-sm">
                      <span className="text-secondary-500 font-mono">{log.date}</span>
                      <span className="text-white font-medium">{log.action}</span>
                      <span className="text-primary-500 font-bold">{log.user}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black flex overflow-hidden">
      {/* LEFT-HAND SIDEBAR MENU */}
      <aside className="w-80 bg-secondary-950 border-r border-primary-500/10 flex flex-col pt-12 shadow-2xl z-20">
        <div className="px-8 mb-12">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center shadow-lg shadow-primary-500/20">
              <SafeIcon icon={FiShield} className="w-6 h-6 text-black" />
            </div>
            <span className="text-[10px] font-black text-primary-500 tracking-[0.2em] uppercase">Control Room</span>
          </div>
          <h2 className="text-xl font-black text-white uppercase tracking-tight">Jedi Master</h2>
        </div>

        <nav className="flex-1 px-4 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveMenu(item.id)}
              className={`w-full flex items-center justify-between px-6 py-4 rounded-xl text-sm font-bold transition-all duration-300 ${
                activeMenu === item.id 
                  ? 'bg-primary-500 text-black shadow-xl shadow-primary-500/10' 
                  : 'text-secondary-500 hover:text-white hover:bg-white/5'
              }`}
            >
              <div className="flex items-center space-x-4">
                <SafeIcon icon={item.icon} className="w-5 h-5" />
                <span className="text-left leading-tight">{item.label}</span>
              </div>
              {activeMenu === item.id && <SafeIcon icon={FiChevronRight} className="w-4 h-4" />}
            </button>
          ))}
        </nav>

        <div className="p-8 bg-black/40 border-t border-white/5 mt-auto">
          <div className="flex items-center space-x-3">
            <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
            <span className="text-[10px] font-black text-secondary-500 uppercase tracking-widest">Master Link Established</span>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 overflow-y-auto bg-[radial-gradient(circle_at_top_right,rgba(240,160,48,0.05),transparent)]">
        <div className="p-12 max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <div className="flex items-end space-x-6 mb-6">
              <div className="p-4 bg-primary-500/10 rounded-2xl border border-primary-500/20">
                <SafeIcon icon={FiShield} className="w-12 h-12 text-primary-500" />
              </div>
              <div>
                <h1 className="text-4xl font-black text-white mb-2">Welcome Jedi Master.</h1>
                <p className="text-lg text-secondary-400 font-medium">You have now accessed the control room for this application.</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-secondary-900/30 backdrop-blur-md p-8 rounded-2xl border border-white/5 shadow-xl">
                <p className="text-[10px] font-black text-secondary-500 uppercase tracking-widest mb-3">Identity Signature</p>
                <p className="text-white font-bold text-lg">Carlo Gunter</p>
              </div>
              <div className="bg-secondary-900/30 backdrop-blur-md p-8 rounded-2xl border border-white/5 shadow-xl">
                <p className="text-[10px] font-black text-secondary-500 uppercase tracking-widest mb-3">Clearance Access</p>
                <p className="text-primary-500 font-black text-lg italic">LEVEL 10 (SYSTEM ROOT)</p>
              </div>
              <div className="bg-secondary-900/30 backdrop-blur-md p-8 rounded-2xl border border-white/5 shadow-xl">
                <p className="text-[10px] font-black text-secondary-500 uppercase tracking-widest mb-3">Host Node</p>
                <p className="text-white font-bold text-lg">AWS-PROD-EAST</p>
              </div>
            </div>
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeMenu}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;